import { toast } from 'react-toastify';
import { useEntityStore } from '@ContractBuilder/store';
import { ApiPatchOperation } from '@domain/schemas/api-patch-operation';
import type { CommentAnchor } from '@domain/schemas/comment.schema';
import { createComment } from '@mutations/createComment';
import { patchComment } from '@mutations/patchComment';
import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

interface AddCommentVars {
  content: string;
  blockId: string;
  threadId: string;
  anchor?: CommentAnchor;
}

// The axios interceptor already toasts the server's message for these, so a second toast would only repeat it.
const isToastedByInterceptor = (error: unknown) => {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  return status !== undefined && status >= 400 && status <= 599 && status !== 401 && status !== 403;
};

const notifyFailure = (message: string) => (error: unknown) => {
  if (!isToastedByInterceptor(error)) {
    toast.error(message);
  }
};

export function useCommentMutations() {
  const getEntityId = () => useEntityStore.getState().submission?.id;

  const addComment = useMutation({
    mutationFn: ({ content, blockId, threadId, anchor }: AddCommentVars) => {
      const entityId = getEntityId();
      if (!entityId) throw new Error('No submission loaded');
      return createComment(
        { content, block_id: blockId, thread_id: threadId, ...(anchor ? { anchor } : {}) },
        entityId,
      );
    },
    onSuccess: () => useEntityStore.getState().reloadSubmission(),
    onError: notifyFailure('Could not post the comment. Please try again.'),
  });

  const resolveConversation = useMutation({
    mutationFn: (threadId: string) => {
      const entityId = getEntityId();
      if (!entityId) throw new Error('No submission loaded');
      return patchComment({ operation: ApiPatchOperation.ResolveCommentsThread, thread_id: threadId }, entityId);
    },
    onSuccess: () => useEntityStore.getState().reloadSubmission(),
    onError: notifyFailure('Could not resolve the conversation. Please try again.'),
  });

  const unresolveConversation = useMutation({
    mutationFn: (threadId: string) => {
      const entityId = getEntityId();
      if (!entityId) throw new Error('No submission loaded');
      return patchComment({ operation: ApiPatchOperation.ReopenCommentsThread, thread_id: threadId }, entityId);
    },
    onSuccess: () => useEntityStore.getState().reloadSubmission(),
    onError: notifyFailure('Could not reopen the conversation. Please try again.'),
  });

  return { addComment, resolveConversation, unresolveConversation };
}
